import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import "./FormControls.css";

interface FieldMeta {
  id: string;
  label: string;
  error?: string;
  hint?: string;
  required?: boolean;
}

function FieldWrapper({
  id,
  label,
  error,
  hint,
  required,
  children,
}: FieldMeta & { children: ReactNode }) {
  return (
    <div className="form-field">
      <label htmlFor={id}>
        {label}
        {required ? <span aria-hidden="true"> *</span> : null}
      </label>
      {children}
      {hint && !error ? (
        <p id={`${id}-hint`} className="form-field-hint">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={`${id}-error`} className="form-field-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

type TextFieldProps = FieldMeta & Omit<InputHTMLAttributes<HTMLInputElement>, keyof FieldMeta>;

export function TextField({ id, label, error, hint, required, ...rest }: TextFieldProps) {
  return (
    <FieldWrapper id={id} label={label} error={error} hint={hint} required={required}>
      <input
        id={id}
        required={required}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        {...rest}
      />
    </FieldWrapper>
  );
}

type TextAreaFieldProps = FieldMeta &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, keyof FieldMeta>;

export function TextAreaField({ id, label, error, hint, required, ...rest }: TextAreaFieldProps) {
  return (
    <FieldWrapper id={id} label={label} error={error} hint={hint} required={required}>
      <textarea
        id={id}
        required={required}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        rows={6}
        {...rest}
      />
    </FieldWrapper>
  );
}

interface SelectFieldProps
  extends FieldMeta, Omit<SelectHTMLAttributes<HTMLSelectElement>, keyof FieldMeta> {
  options: { value: string; label: string }[];
}

export function SelectField({
  id,
  label,
  error,
  hint,
  required,
  options,
  ...rest
}: SelectFieldProps) {
  return (
    <FieldWrapper id={id} label={label} error={error} hint={hint} required={required}>
      <select
        id={id}
        required={required}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        {...rest}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </FieldWrapper>
  );
}

interface CheckboxFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "id" | "label"> {
  id: string;
  label: ReactNode;
  error?: string;
}

export function CheckboxField({ id, label, error, ...rest }: CheckboxFieldProps) {
  return (
    <div className="form-field form-field-checkbox">
      <div className="form-checkbox-row">
        <input
          id={id}
          type="checkbox"
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          {...rest}
        />
        <label htmlFor={id}>{label}</label>
      </div>
      {error ? (
        <p id={`${id}-error`} className="form-field-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
